import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  UserPlus, X, Users, Flame, Trash2, Loader2, Mail, CheckCircle, Clock,
  Pencil, Check, RefreshCw
} from "lucide-react";

const CorporateEmployees = () => {
  const { token } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteEmails, setInviteEmails] = useState("");
  const [selectedProgram, setSelectedProgram] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);
  const [resending, setResending] = useState(null);

  const fetchData = async () => {
    try {
      const [empRes, progRes] = await Promise.all([
        fetch(`${API}/corporate/employees`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/corporate/programs`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (empRes.ok) { const d = await empRes.json(); setEmployees(d.employees || []); }
      if (progRes.ok) { const d = await progRes.json(); setPrograms(d.programs || []); }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [token]); // eslint-disable-line

  const handleInvite = async () => {
    const emails = inviteEmails.split(/[,\n]+/).map(e => e.trim()).filter(Boolean);
    if (emails.length === 0) { toast.error("Enter at least one email"); return; }
    setInviting(true);
    try {
      const body = { emails };
      if (selectedProgram) body.program_id = selectedProgram;
      const res = await fetch(`${API}/corporate/employees/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        const data = await res.json();
        const invited = data.results?.filter(r => r.status === "invited").length || 0;
        const existing = data.results?.filter(r => r.status === "already_exists").length || 0;
        if (invited > 0) toast.success(`${invited} employee(s) invited! They'll receive an email with login credentials.`);
        if (existing > 0) toast.info(`${existing} email(s) already exist on the platform.`);
        setShowInvite(false); setInviteEmails(""); setSelectedProgram("");
        fetchData();
      } else { const err = await res.json(); toast.error(err.detail || "Failed to invite"); }
    } catch (err) { toast.error("Error inviting employees"); }
    finally { setInviting(false); }
  };

  const handleUpdateName = async (userId) => {
    if (!editName.trim()) { toast.error("Name cannot be empty"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/corporate/employees/${userId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: editName.trim() })
      });
      if (res.ok) { toast.success("Name updated"); setEditingId(null); fetchData(); }
      else { const err = await res.json(); toast.error(err.detail || "Failed to update"); }
    } catch (err) { toast.error("Error updating"); }
    finally { setSaving(false); }
  };

  const handleResendInvite = async (userId, email) => {
    setResending(userId);
    try {
      const res = await fetch(`${API}/corporate/employees/${userId}/resend-invite`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || `Invite resent to ${email}`);
      } else { const err = await res.json(); toast.error(err.detail || "Failed to resend"); }
    } catch (err) { toast.error("Error resending invite"); }
    finally { setResending(null); }
  };

  const handleDelete = async (userId, name) => {
    if (!window.confirm(`Remove "${name}" from your wellness program? Their account and all enrollment data will be deleted.`)) return;
    try {
      const res = await fetch(`${API}/corporate/employees/${userId}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { toast.success("Employee removed"); fetchData(); }
      else { const err = await res.json(); toast.error(err.detail || "Failed to remove"); }
    } catch (err) { toast.error("Error removing employee"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-secondary">Employees</h2>
          <p className="text-sm text-muted-foreground">Invite employees and manage program participation</p>
        </div>
        <Button onClick={() => setShowInvite(!showInvite)} className="bg-emerald-600 hover:bg-emerald-700 rounded-xl" data-testid="invite-employees-btn">
          {showInvite ? <X className="w-4 h-4 mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
          {showInvite ? "Cancel" : "Invite Employees"}
        </Button>
      </div>

      {showInvite && (
        <Card className="rounded-2xl border-emerald-200 p-5 space-y-4" data-testid="invite-form">
          <h3 className="font-bold text-secondary">Invite by Email</h3>
          <p className="text-sm text-muted-foreground">
            Each employee will receive a welcome email with their login credentials and instructions to join the challenge.
          </p>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Employee Emails (comma or newline separated)</label>
            <Input placeholder="e.g. john@company.com, jane@company.com" value={inviteEmails} onChange={(e) => setInviteEmails(e.target.value)} className="rounded-xl" data-testid="invite-emails-input" />
          </div>
          {programs.length > 0 && (
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Auto-enroll in Program (optional)</label>
              <Select value={selectedProgram} onValueChange={setSelectedProgram}>
                <SelectTrigger className="rounded-xl" data-testid="invite-program-select"><SelectValue placeholder="Select a program to auto-enroll" /></SelectTrigger>
                <SelectContent>
                  {programs.map((p) => (<SelectItem key={p.program_id} value={p.program_id}>{p.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button onClick={handleInvite} disabled={inviting} className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-xl" data-testid="submit-invite-btn">
            {inviting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
            Send Invitations
          </Button>
        </Card>
      )}

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="animate-pulse h-20 bg-white rounded-2xl" />)}</div>
      ) : employees.length === 0 ? (
        <Card className="rounded-2xl p-8 text-center border-emerald-100">
          <Users className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
          <p className="text-muted-foreground">No employees yet. Invite your team to get started!</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {employees.map((emp) => {
            const enrolled = emp.enrollment;
            const isEditing = editingId === emp.user_id;

            return (
              <Card key={emp.user_id} className="rounded-2xl border-emerald-100/50" data-testid={`employee-${emp.user_id}`}>
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-xs sm:text-sm shrink-0">
                        {(emp.challenge_alias || emp.name || "?")[0].toUpperCase()}
                      </div>
                      <div>
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <Input
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              className="rounded-lg h-8 w-48 text-sm"
                              autoFocus
                              onKeyDown={(e) => e.key === "Enter" && handleUpdateName(emp.user_id)}
                              data-testid="edit-employee-name-input"
                            />
                            <Button size="sm" variant="ghost" onClick={() => handleUpdateName(emp.user_id)} disabled={saving} className="h-8 w-8 p-0 text-emerald-600" data-testid="save-employee-name">
                              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} className="h-8 w-8 p-0 text-muted-foreground">
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-secondary">{emp.name}</span>
                            {emp.challenge_alias && (
                              <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{emp.challenge_alias}</Badge>
                            )}
                            <Button
                              size="sm" variant="ghost"
                              onClick={() => { setEditingId(emp.user_id); setEditName(emp.name); }}
                              className="h-6 w-6 p-0 text-muted-foreground hover:text-emerald-600"
                              data-testid={`edit-name-${emp.user_id}`}
                            >
                              <Pencil className="w-3 h-3" />
                            </Button>
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground">{emp.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 pl-12 sm:pl-0 shrink-0">
                      {enrolled ? (
                        <div className="flex items-center gap-2 sm:gap-3 text-sm">
                          <div className="flex items-center gap-1 text-orange-500">
                            <Flame className="w-4 h-4" />
                            <span className="font-bold">{enrolled.streak_count || 0}</span>
                          </div>
                          <Badge className={enrolled.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}>
                            <CheckCircle className="w-3 h-3 mr-1" />{enrolled.status || "enrolled"}
                          </Badge>
                        </div>
                      ) : (
                        <Badge className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 mr-1" />Not enrolled</Badge>
                      )}
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => handleResendInvite(emp.user_id, emp.email)}
                        disabled={resending === emp.user_id}
                        className="text-muted-foreground hover:text-blue-500"
                        title="Resend invite email"
                        data-testid={`resend-invite-${emp.user_id}`}
                      >
                        {resending === emp.user_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => handleDelete(emp.user_id, emp.name)}
                        className="text-muted-foreground hover:text-red-500"
                        data-testid={`delete-employee-${emp.user_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CorporateEmployees;
